import { DonationType } from './donation-flow.models';

/**
 * Route data attached to every donation step. Consumers read it via a
 * typed helper (see `readDonationRouteData`) so route reading stays type-safe.
 */
export interface DonationRouteData {
  donationType: DonationType;
  step: number;
}

/**
 * Declarative shape of a donation flow (OneTime = 3 steps, Recurring = 4).
 * Step 1 is always the `DonationComponent`; the final step is always
 * `ThankYouStepComponent`. Intermediate steps are declared here.
 */
export interface DonationRouteConfig {
  donationType: DonationType;
  /** Step number → tag consumed by the route builder to pick a component. */
  intermediateSteps: ReadonlyArray<{
    step: number;
    componentTag: 'details' | 'recurring-confirmation';
  }>;
  finalStep: number;
}

export const DONATION_ROUTE_CONFIGS: readonly DonationRouteConfig[] = [
  {
    donationType: DonationType.OneTime,
    intermediateSteps: [{ step: 2, componentTag: 'details' }],
    finalStep: 3,
  },
  {
    donationType: DonationType.Recurring,
    intermediateSteps: [
      { step: 2, componentTag: 'details' },
      { step: 3, componentTag: 'recurring-confirmation' },
    ],
    finalStep: 4,
  },
] as const;

/** All valid step numbers for a given donation type, including 1 and the final step. */
export function getValidStepsFor(donationType: DonationType): readonly number[] {
  const config = DONATION_ROUTE_CONFIGS.find(
    (c) => c.donationType === donationType
  );
  if (!config) return [];
  return [1, ...config.intermediateSteps.map((s) => s.step), config.finalStep];
}

export function isValidStepFor(
  donationType: DonationType,
  step: number
): boolean {
  return getValidStepsFor(donationType).includes(step);
}

export function getFinalStep(donationType: DonationType): number {
  const config = DONATION_ROUTE_CONFIGS.find(
    (c) => c.donationType === donationType
  );
  return config?.finalStep ?? 1;
}

/**
 * Segments for `router.navigate` — used by every navigation site so no
 * component or effect concatenates `/one-time/step/2` by hand.
 *
 * @example
 *   getDonationStepPath(DonationType.Recurring, 3) // ['recurring', 'step', '3']
 */
export function getDonationStepPath(
  donationType: DonationType,
  step: number
): string[] {
  return [donationType, 'step', String(step)];
}
